import { NCALayerModes, StorageKeys } from "@/config/consts";
import { Api, TokenApi } from "@/services/api.service";
import { ApiCatch, Catch } from "@/utils/decorators";
import { SignService } from "./sign.service";
import { AuthModule } from "@/store/auth.module";
import router from "@/router";
import { SignKey } from "~/eds.type";
import { makeMessageForTranslate } from "@/utils";

export class AuthService {
  /**
   * Checks local token for existence
   */
  public static hasToken(): boolean {
    const token = this.getToken();
    return token != null;
  }

  public static setToken(token: string): void {
    localStorage.setItem(StorageKeys.TOKEN, token);
    AuthModule.setIsLoggedIn(true);
  }

  public static getToken(): string | null {
    return localStorage.getItem(StorageKeys.TOKEN);
  }

  /**
   * @debug
   * @description Authenticates user by bin-iin-permission-key
   * combination, stores access token in the localStorage
   *
   * @returns {boolean} authentication result, if true then
   * authenticated, false otherwise
   */
  @Catch(e => ({
    result: e?.response?.data.message,
  }))
  public static async authenticateTest(
    bin: string,
    iin: string,
    extendedKeyUsage: string,
  ): Promise<MessageValue<boolean>> {
    const response = await Api.post("/auth/signin_debug", {
      bin,
      iin,
      extendedKeyUsage,
    });

    const token = response.data.accessToken;
    AuthService.setToken(token);

    return { result: true };
  }

  /**
   * @description Sign XML with EDS and send it for authentication.
   */
  @Catch(e => ({
    result: e?.response?.data.message,
  }))
  public static async authenticate(
    storageType: string,
  ): Promise<MessageValue<boolean | ApiMessageObject>> {
    let response = await Api.get("/auth/XmlForSignature");
    const action: SignKey = "AUTHENTICATION";

    const { result, message } = await SignService.signXML(
      storageType,
      response.data,
      { signKey: action, signMethod: NCALayerModes.xml },
    );
    if (message) throw message;

    const {
      result: v_result,
      message: v_message,
    } = await SignService.validateEDS(result as string, action);
    if (!v_result) {
      throw makeMessageForTranslate(v_message as ApiMessageObject);
    }

    response = await Api.post("auth/signin", {
      signedData: result,
    });

    const token = response.data.accessToken;
    AuthService.setToken(token);

    return { result: true };
  }

  /**
   * @description Refreshes local token
   */
  @ApiCatch
  public static async refreshToken() {
    const localToken = AuthService.getToken();
    // if there's no local token, do not try refreshing it
    if (!localToken) return { status: 400 };
    const response = await TokenApi.post("/auth/refreshToken");

    const token = response.data.accessToken;
    AuthService.setToken(token);

    return response;
  }

  /**
   * @description Logout the user, removes all
   * local data, and forwards router to the auth page
   */
  public static async logout(needRedirect: boolean = true) {
    AuthModule.setIsLoggedIn(false);
    localStorage.removeItem(StorageKeys.TOKEN);
    if (needRedirect) {
      router.push("/auth");
    }
  }

  /**
   * @description Fetches the user info
   */
  @ApiCatch
  public static async getUser() {
    return Api.get("/auth/user");
  }

  /**
   * @description Fetches user's permissions list
   */
  @Catch(() => [])
  public static async getPermissions() {
    const response = await Api.get("/permissions");
    return response.data;
  }
}
